(function (globalScope, factory) {
  var api = factory(globalScope);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  globalScope.GozoStoreLocator = api;

  function start(root) {
    api.initAll(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { start(document); }, { once: true });
  } else {
    start(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    start(event.target);
  });
})(typeof window !== 'undefined' ? window : globalThis, function (globalScope) {
  'use strict';

  var EARTH_RADIUS_MILES = 3958.8;
  var DEFAULT_CENTER = [-111.891, 40.76];
  var DEFAULT_ZOOM = 4;
  var MAPBOX_JS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.26.0/mapbox-gl.js';
  var MAPBOX_CSS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.26.0/mapbox-gl.css';
  var mapboxLoaderPromise = null;

  function toNumber(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function loadMapbox() {
    if (globalScope.mapboxgl) return Promise.resolve(globalScope.mapboxgl);
    if (mapboxLoaderPromise) return mapboxLoaderPromise;

    mapboxLoaderPromise = new Promise(function (resolve, reject) {
      var stylesheet = document.querySelector('link[data-gozo-mapbox-css]');
      if (!stylesheet) {
        stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = MAPBOX_CSS_URL;
        stylesheet.setAttribute('data-gozo-mapbox-css', '');
        document.head.appendChild(stylesheet);
      }

      var script = document.querySelector('script[data-gozo-mapbox-js]');
      var handleLoad = function () {
        if (globalScope.mapboxgl) {
          resolve(globalScope.mapboxgl);
        } else {
          reject(new Error('Mapbox loaded without its browser API.'));
        }
      };

      if (!script) {
        script = document.createElement('script');
        script.src = MAPBOX_JS_URL;
        script.defer = true;
        script.setAttribute('data-gozo-mapbox-js', '');
      }

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', function () {
        reject(new Error('Mapbox browser assets could not be loaded.'));
      }, { once: true });

      if (!script.parentNode) document.head.appendChild(script);
    }).catch(function (error) {
      mapboxLoaderPromise = null;
      throw error;
    });

    return mapboxLoaderPromise;
  }

  function stateSearchTerms(address) {
    var match = String(address || '').match(/,\s*(UT|TX)\b/i);
    if (!match) return '';
    return match[1].toUpperCase() === 'UT' ? 'UT Utah' : 'TX Texas';
  }

  function haversineMiles(origin, destination) {
    var toRadians = function (degrees) { return degrees * Math.PI / 180; };
    var lat1 = toRadians(origin.lat);
    var lat2 = toRadians(destination.lat);
    var deltaLat = toRadians(destination.lat - origin.lat);
    var deltaLng = toRadians(destination.lng - origin.lng);
    var a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
      + Math.cos(lat1) * Math.cos(lat2)
      * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_MILES * c;
  }

  function rankLocations(locations, origin, radius) {
    var ranked = locations
      .filter(function (location) {
        return Number.isFinite(location.lat) && Number.isFinite(location.lng);
      })
      .map(function (location) {
        return Object.assign({}, location, {
          distance: haversineMiles(origin, location)
        });
      })
      .sort(function (left, right) { return left.distance - right.distance; });

    var withinRadius = ranked.filter(function (location) {
      return radius === Infinity || location.distance <= radius;
    });

    return {
      ranked: ranked,
      withinRadius: withinRadius,
      results: withinRadius.length > 0 ? withinRadius : ranked.slice(0, 3),
      usedNearestFallback: withinRadius.length === 0 && ranked.length > 0
    };
  }

  function filterLocalLocations(locations, query) {
    var needle = String(query || '').trim().toLowerCase();
    if (!needle) return locations.slice();

    return locations.filter(function (location) {
      return [location.name, location.address, location.zip, location.area, location.state]
        .join(' ')
        .toLowerCase()
        .indexOf(needle) !== -1;
    });
  }

  function isPostalQuery(query) {
    return /^\d{5}(?:-\d{4})?$/.test(String(query || '').trim());
  }

  function readLocations(sectionElement) {
    return Array.prototype.map.call(
      sectionElement.querySelectorAll('.wtfu__loc-btn'),
      function (button) {
        return {
          id: button.getAttribute('data-location-id') || '',
          name: button.getAttribute('data-name') || '',
          address: button.getAttribute('data-address') || '',
          area: button.getAttribute('data-area') || '',
          state: stateSearchTerms(button.getAttribute('data-address')),
          zip: button.getAttribute('data-zip') || '',
          lat: toNumber(button.getAttribute('data-lat')),
          lng: toNumber(button.getAttribute('data-lng')),
          button: button
        };
      }
    );
  }

  function StoreLocator(sectionElement) {
    this.sectionElement = sectionElement;
    this.sectionId = sectionElement.id || 'gozo-locator';
    this.token = sectionElement.getAttribute('data-mapbox-token') || '';
    this.mapElement = sectionElement.querySelector('.wtfu__map');
    this.mapStateElement = sectionElement.querySelector('.wtfu__map-state');
    this.form = sectionElement.querySelector('.wtfu__search-form');
    this.searchInput = sectionElement.querySelector('.wtfu__search');
    this.radiusSelect = sectionElement.querySelector('.wtfu__radius');
    this.statusElement = sectionElement.querySelector('.wtfu__search-status');
    this.emptyElement = sectionElement.querySelector('.wtfu__search-empty');
    this.listElement = sectionElement.querySelector('.wtfu__list');
    this.buttons = Array.prototype.slice.call(sectionElement.querySelectorAll('.wtfu__loc-btn'));
    this.areaLabels = Array.prototype.slice.call(sectionElement.querySelectorAll('.wtfu__area-label'));
    this.locations = readLocations(sectionElement);
    this.locationsById = this.locations.reduce(function (lookup, location) {
      lookup[location.id] = location;
      return lookup;
    }, {});
    this.currentLocations = this.locations.slice();
    this.map = null;
    this.mapPromise = null;
    this.popup = null;
    this.searchMarker = null;
    this.geolocateControl = null;
    this.locationMarkers = {};
    this.lastSearch = null;

    this.bindEvents();
    this.applyLocations(this.locations, { message: 'Showing all ' + this.locations.length + ' locations.' });
    this.observeMap();
  }

  StoreLocator.prototype.bindEvents = function () {
    var locator = this;

    this.buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        locator.selectLocation(button.getAttribute('data-location-id'), true);
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        locator.search(locator.searchInput ? locator.searchInput.value : '');
      });
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('search', function () {
        if (!locator.searchInput.value.trim()) locator.reset();
      });
    }

    if (this.radiusSelect) {
      this.radiusSelect.addEventListener('change', function () {
        if (locator.lastSearch) {
          locator.showNearby(locator.lastSearch.origin, locator.lastSearch.label, {
            showSearchMarker: locator.lastSearch.showSearchMarker
          });
        }
      });
    }
  };

  StoreLocator.prototype.observeMap = function () {
    var locator = this;

    if (!this.token) {
      this.setMapState('Map setup is incomplete. Browse the retailer list for locations.');
      return;
    }

    if (!this.locations.some(function (location) {
      return Number.isFinite(location.lat) && Number.isFinite(location.lng);
    })) {
      this.setMapState('Map coordinates have not been added yet.');
      return;
    }

    if (typeof IntersectionObserver === 'undefined') return;

    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      locator.ensureMap();
    }, { rootMargin: '200px 0px' });

    observer.observe(this.sectionElement);
  };

  StoreLocator.prototype.setMapState = function (message, hidden) {
    if (!this.mapStateElement) return;
    this.mapStateElement.hidden = Boolean(hidden);
    if (message) this.mapStateElement.textContent = message;
  };

  StoreLocator.prototype.setStatus = function (message) {
    if (this.statusElement) this.statusElement.textContent = message;
  };

  StoreLocator.prototype.setBusy = function (isBusy) {
    if (this.form) this.form.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    if (this.searchInput) this.searchInput.disabled = isBusy;
    if (this.radiusSelect) this.radiusSelect.disabled = isBusy;
    var submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
    if (submitButton) submitButton.disabled = isBusy;
  };

  StoreLocator.prototype.useCurrentLocation = function (coordinates) {
    var origin = {
      lng: Number(coordinates && coordinates.longitude),
      lat: Number(coordinates && coordinates.latitude)
    };

    if (!Number.isFinite(origin.lng) || !Number.isFinite(origin.lat)) {
      this.setStatus('We could not read your location. Check your browser permission or search by ZIP.');
      return;
    }

    this.lastSearch = {
      origin: origin,
      label: 'your location',
      showSearchMarker: false
    };
    this.showNearby(origin, 'your location', { showSearchMarker: false });
  };

  StoreLocator.prototype.ensureMap = function () {
    var locator = this;
    if (this.mapPromise) return this.mapPromise;
    if (!locator.token || !locator.mapElement) {
      locator.setMapState('The interactive map is temporarily unavailable. Browse the location list instead.');
      return Promise.reject(new Error('Mapbox could not be initialized.'));
    }

    this.mapPromise = loadMapbox().then(function () {
      return new Promise(function (resolve, reject) {
        if (typeof globalScope.mapboxgl === 'undefined') {
          var unavailable = new Error('Mapbox could not be initialized.');
          locator.setMapState('The interactive map is temporarily unavailable. Browse the location list instead.');
          reject(unavailable);
          return;
        }

        locator.setMapState('Loading retailer map…');
        globalScope.mapboxgl.accessToken = locator.token;

        var map = new globalScope.mapboxgl.Map({
          container: locator.mapElement,
          style: 'mapbox://styles/mapbox/standard',
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          attributionControl: true,
          cooperativeGestures: true
        });

        locator.map = map;
        map.addControl(new globalScope.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

        locator.geolocateControl = new globalScope.mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          fitBoundsOptions: { maxZoom: 12 },
          trackUserLocation: false,
          showAccuracyCircle: true
        });
        map.addControl(locator.geolocateControl, 'top-right');

        locator.geolocateControl.on('geolocate', function (event) {
          locator.useCurrentLocation(event.coords);
        });

        locator.geolocateControl.on('error', function () {
          locator.setStatus('We could not access your location. Check your browser permission or search by ZIP.');
        });

        map.once('load', function () {
          locator.addMapMarkers();
          locator.setMapState('', true);
          locator.fitLocations(locator.currentLocations);
          resolve(map);
        });

        map.once('error', function (event) {
          if (map.loaded()) return;
          var error = event && event.error ? event.error : new Error('Mapbox failed to load.');
          locator.setMapState('The interactive map is temporarily unavailable. Browse the location list instead.');
          reject(error);
        });
      });
    });

    this.mapPromise.catch(function () {
      locator.mapPromise = null;
    });

    return this.mapPromise;
  };

  StoreLocator.prototype.createMapPin = function (location) {
    var locator = this;
    var element = document.createElement('button');
    var shape = document.createElement('span');

    element.type = 'button';
    element.className = 'wtfu__map-pin';
    element.setAttribute('aria-label', 'View ' + location.name);
    element.setAttribute('aria-pressed', 'false');
    shape.className = 'wtfu__map-pin-shape';
    shape.setAttribute('aria-hidden', 'true');
    element.appendChild(shape);
    element.addEventListener('click', function (event) {
      event.stopPropagation();
      locator.selectLocation(location.id, true);
    });

    return element;
  };

  StoreLocator.prototype.addMapMarkers = function () {
    var locator = this;
    if (!this.map) return;

    this.locations.forEach(function (location) {
      if (locator.locationMarkers[location.id]) return;
      if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng)) return;

      var element = locator.createMapPin(location);
      var marker = new globalScope.mapboxgl.Marker({
        element: element,
        anchor: 'bottom'
      })
        .setLngLat([location.lng, location.lat])
        .addTo(locator.map);
      element.setAttribute('role', 'button');

      locator.locationMarkers[location.id] = {
        element: element,
        marker: marker
      };
    });

    this.updateMapData(this.currentLocations);
  };

  StoreLocator.prototype.updateMapData = function (locations) {
    var locator = this;
    this.currentLocations = locations.slice();
    var visibleIds = new Set(locations.map(function (location) { return location.id; }));

    Object.keys(this.locationMarkers).forEach(function (locationId) {
      locator.locationMarkers[locationId].element.hidden = !visibleIds.has(locationId);
    });
  };

  StoreLocator.prototype.fitLocations = function (locations, origin) {
    if (!this.map || typeof globalScope.mapboxgl === 'undefined') return;
    var coordinates = locations
      .filter(function (location) {
        return Number.isFinite(location.lat) && Number.isFinite(location.lng);
      })
      .map(function (location) { return [location.lng, location.lat]; });

    if (origin) coordinates.push([origin.lng, origin.lat]);
    if (coordinates.length === 0) return;
    if (coordinates.length === 1) {
      this.map.flyTo({ center: coordinates[0], zoom: 12, essential: true });
      return;
    }

    var bounds = coordinates.reduce(function (currentBounds, coordinate) {
      return currentBounds.extend(coordinate);
    }, new globalScope.mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

    this.map.fitBounds(bounds, { padding: 54, maxZoom: 12, duration: 700 });
  };

  StoreLocator.prototype.createPopupContent = function (location) {
    var content = document.createElement('div');
    content.className = 'wtfu__popup';

    var name = document.createElement('strong');
    name.className = 'wtfu__popup-name';
    name.textContent = location.name;
    content.appendChild(name);

    var address = document.createElement('span');
    address.className = 'wtfu__popup-address';
    address.textContent = location.address + (location.zip ? ' ' + location.zip : '');
    content.appendChild(address);

    var directions = document.createElement('a');
    directions.className = 'wtfu__popup-link';
    directions.href = 'https://www.google.com/maps/dir/?api=1&destination='
      + encodeURIComponent(location.lat + ',' + location.lng);
    directions.target = '_blank';
    directions.rel = 'noopener';
    directions.textContent = 'Get directions';
    content.appendChild(directions);

    return content;
  };

  StoreLocator.prototype.selectLocation = function (locationId, shouldFocusMap) {
    var locator = this;
    var location = this.locationsById[locationId];
    if (!location) return;

    this.buttons.forEach(function (button) {
      var isActive = button.getAttribute('data-location-id') === locationId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    Object.keys(this.locationMarkers).forEach(function (markerId) {
      var markerElement = locator.locationMarkers[markerId].element;
      var isActive = markerId === locationId;
      markerElement.classList.toggle('is-active', isActive);
      markerElement.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (location.button && shouldFocusMap) {
      location.button.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    this.ensureMap().then(function (map) {
      if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng)) return;
      map.flyTo({ center: [location.lng, location.lat], zoom: 13, essential: true });
      if (locator.popup) locator.popup.remove();
      locator.popup = new globalScope.mapboxgl.Popup({ offset: 32, closeButton: true })
        .setLngLat([location.lng, location.lat])
        .setDOMContent(locator.createPopupContent(location))
        .addTo(map);
    }).catch(function () {
      locator.setStatus('The map is unavailable, but the retailer address is still listed.');
    });
  };

  StoreLocator.prototype.updateAreaLabels = function () {
    var locator = this;
    this.areaLabels.forEach(function (label) {
      var sibling = label.nextElementSibling;
      var hasVisibleButton = false;
      while (sibling && !sibling.classList.contains('wtfu__area-label')) {
        if (sibling.classList.contains('wtfu__loc-btn') && !sibling.hidden) {
          hasVisibleButton = true;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      label.hidden = !hasVisibleButton;
    });

    if (locator.listElement) locator.listElement.classList.remove('is-proximity-results');
  };

  StoreLocator.prototype.applyLocations = function (locations, options) {
    options = options || {};
    var visibleIds = new Set(locations.map(function (location) { return location.id; }));
    var proximityMode = Boolean(options.proximity);
    var rankById = {};

    locations.forEach(function (location, index) {
      rankById[location.id] = { rank: index + 1, distance: location.distance };
    });

    this.locations.forEach(function (location) {
      var isVisible = visibleIds.has(location.id);
      var distanceElement = location.button.querySelector('.wtfu__loc-distance');
      location.button.hidden = !isVisible;
      location.button.style.order = proximityMode && isVisible ? String(rankById[location.id].rank) : '';
      if (distanceElement) {
        distanceElement.hidden = !proximityMode || !isVisible;
        distanceElement.textContent = proximityMode && isVisible
          ? rankById[location.id].distance.toFixed(1) + ' miles away'
          : '';
      }
    });

    if (proximityMode) {
      this.areaLabels.forEach(function (label) { label.hidden = true; });
      if (this.listElement) this.listElement.classList.add('is-proximity-results');
    } else {
      this.updateAreaLabels();
    }

    if (this.emptyElement) this.emptyElement.hidden = locations.length > 0;
    this.setStatus(options.message || '');
    this.updateMapData(locations);

    if (this.map) this.fitLocations(locations, options.origin);
  };

  StoreLocator.prototype.getRadius = function () {
    var value = this.radiusSelect ? this.radiusSelect.value : '50';
    return value === 'all' ? Infinity : (toNumber(value) || 50);
  };

  StoreLocator.prototype.search = function (rawQuery) {
    var query = String(rawQuery || '').trim();
    if (!query) {
      this.reset();
      return;
    }

    if (!isPostalQuery(query)) {
      var localMatches = filterLocalLocations(this.locations, query);
      if (localMatches.length > 0) {
        this.lastSearch = null;
        this.removeSearchMarker();
        this.applyLocations(localMatches, {
          message: localMatches.length + (localMatches.length === 1 ? ' location matches “' : ' locations match “') + query + '”.'
        });
        if (this.map) this.fitLocations(localMatches);
        return;
      }
    }

    this.geocode(query);
  };

  StoreLocator.prototype.geocode = function (query) {
    var locator = this;
    if (!this.token) {
      this.setStatus('ZIP search is not configured yet.');
      return;
    }

    this.setBusy(true);
    this.setStatus('Searching near ' + query + '…');

    var types = isPostalQuery(query) ? 'postcode' : 'postcode,place,region';
    var url = 'https://api.mapbox.com/search/geocode/v6/forward?q=' + encodeURIComponent(query)
      + '&country=us&types=' + encodeURIComponent(types)
      + '&autocomplete=false&limit=1&access_token=' + encodeURIComponent(this.token);

    this.ensureMap().catch(function () {});

    fetch(url).then(function (response) {
      if (!response.ok) throw new Error('ZIP search request failed.');
      return response.json();
    }).then(function (data) {
      var feature = data.features && data.features[0];
      var coordinates = feature && feature.geometry && feature.geometry.coordinates;
      if (!coordinates || coordinates.length < 2) throw new Error('Location not found.');

      var origin = { lng: Number(coordinates[0]), lat: Number(coordinates[1]) };
      locator.lastSearch = { origin: origin, label: query, showSearchMarker: true };
      locator.showNearby(origin, query);
    }).catch(function () {
      locator.setStatus('We could not find that ZIP code or city. Try another search.');
    }).finally(function () {
      locator.setBusy(false);
    });
  };

  StoreLocator.prototype.showNearby = function (origin, label, options) {
    options = options || {};
    var radius = this.getRadius();
    var ranked = rankLocations(this.locations, origin, radius);
    var radiusLabel = radius === Infinity ? 'any distance' : radius + ' miles';
    var message;

    if (ranked.usedNearestFallback) {
      message = 'No retailers are within ' + radiusLabel + ' of ' + label + '. Showing the nearest three.';
    } else if (radius === Infinity) {
      message = 'Showing all ' + ranked.results.length + ' retailers ordered by distance from ' + label + '.';
    } else {
      message = ranked.results.length + (ranked.results.length === 1 ? ' retailer is' : ' retailers are')
        + ' within ' + radiusLabel + ' of ' + label + '.';
    }

    this.applyLocations(ranked.results, {
      message: message,
      proximity: true,
      origin: origin
    });
    if (options.showSearchMarker === false) {
      this.removeSearchMarker();
    } else {
      this.showSearchMarker(origin, label);
    }
    if (this.map) this.fitLocations(ranked.results, origin);
  };

  StoreLocator.prototype.showSearchMarker = function (origin, label) {
    var locator = this;
    this.ensureMap().then(function (map) {
      locator.removeSearchMarker();
      var element = document.createElement('div');
      element.className = 'wtfu__search-marker';
      element.setAttribute('aria-label', 'Search location: ' + label);
      locator.searchMarker = new globalScope.mapboxgl.Marker({ element: element })
        .setLngLat([origin.lng, origin.lat])
        .addTo(map);
    }).catch(function () {});
  };

  StoreLocator.prototype.removeSearchMarker = function () {
    if (!this.searchMarker) return;
    this.searchMarker.remove();
    this.searchMarker = null;
  };

  StoreLocator.prototype.reset = function () {
    this.lastSearch = null;
    this.removeSearchMarker();
    if (this.searchInput) this.searchInput.value = '';
    this.applyLocations(this.locations, { message: 'Showing all ' + this.locations.length + ' locations.' });
    if (this.map) this.fitLocations(this.locations);
  };

  function initAll(root) {
    var searchRoot = root && root.querySelectorAll ? root : document;
    var sections = [];

    if (searchRoot.matches && searchRoot.matches('[data-gozo-store-locator]')) sections.push(searchRoot);
    sections = sections.concat(Array.prototype.slice.call(searchRoot.querySelectorAll('[data-gozo-store-locator]')));

    sections.forEach(function (sectionElement) {
      if (sectionElement.getAttribute('data-locator-initialized') === 'true') return;
      sectionElement.setAttribute('data-locator-initialized', 'true');
      new StoreLocator(sectionElement);
    });
  }

  return {
    StoreLocator: StoreLocator,
    filterLocalLocations: filterLocalLocations,
    haversineMiles: haversineMiles,
    initAll: initAll,
    isPostalQuery: isPostalQuery,
    rankLocations: rankLocations,
    toNumber: toNumber
  };
});
